import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { formatDate } from '@/lib/format'
import { HistoryPanel } from './HistoryPanel'
import {
  fetchBoard,
  fetchConversion,
  fetchFunnel,
  fetchTimeToFill,
  holdVacancy,
  moveApplication,
  openVacancy,
  rejectApplication,
  revokeConsent,
  type ApplicationCard,
  type ConversionView,
} from './hrApi'
import {
  FormModal,
  OutcomeAlerts,
  RefLink,
  Section,
  StatusBadge,
  calendarDays,
  day,
  money,
  number,
  num,
  percent,
  short,
  text,
  today,
  useOutcome,
  type Translate,
} from './hrShared'
import {
  ConfirmModal,
  Facts,
  MoreMenu,
  Panel,
  RecordHeader,
  RecordTabs,
  StageBar,
  StateView,
  type Fact,
  type HeaderAction,
  type MenuAction,
  type StageStep,
} from './record'
import { labelOf, listPath } from './recordHelpers'
import type { RecordViewProps } from './types'
import '@/styles/record.css'

/** Порядок стадий отклика — тот же, что у сервера: доска читается слева направо. */
export const STAGES = ['applied', 'screening', 'interview', 'offer', 'hired'] as const

/**
 * Карточка вакансии на дизайн-системе (Vacancy.dc.html): канбан кандидатов и воронка найма.
 *
 * Поля вакансии берутся из механизма списков: чтения одной вакансии (`GET /hr/vacancies/{id}`)
 * на сервере нет, а обёртка, гарантированно возвращающая 404, выглядела бы рабочей.
 *
 * Доска показывает **открытые** отклики — работу, которую надо делать. Воронка считает
 * **все**, включая отказы и нанятых, и поэтому приходит своим запросом, а не подсчётом
 * колонок доски: стадия, с которой все уходят в отказ, на доске выглядит пустой и
 * благополучной.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - Опыт и источник кандидатов: отклик несёт только имя кандидата (`candidateName`, под тем
 *   же правом, что открывает доску). Кандидата нет в механизме списков, и заводить его там
 *   ради карточки значило бы открыть имена всех когда-либо зарегистрированных.
 * - «Добавить кандидата», «Опубликовать на работных сайтах», «Ссылка для откликов»,
 *   «Изменить требования», описание вакансии: регистрация кандидата требует ПДн и согласия
 *   отдельной формой, публикации и описания REST не выводит.
 * - «Закрыть вакансию»: эндпоинта закрытия нет — вакансия закрывается наймом.
 * - Интервью, команда подбора сверх рекрутера и нанимающего менеджера, источники откликов.
 */
export function VacancyRecordView({ recordId, record, context, form }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const values = record.values
  const [tab, setTab] = useState('candidates')
  const status = text(values, 'status')
  const mayUpdate = can(user, 'hr.vacancy', 'update')
  const mayReadCandidates = can(user, 'hr.candidate', 'read')

  const funnel = useQuery({
    queryKey: ['hr-funnel', recordId],
    queryFn: ({ signal }) => fetchFunnel(recordId, signal),
    retry: false,
  })
  const board = useQuery({
    queryKey: ['hr-board', recordId],
    queryFn: ({ signal }) => fetchBoard(recordId, signal),
    enabled: mayReadCandidates,
    retry: false,
  })

  const outcome = useOutcome([
    ['record', 'hr.vacancy', recordId],
    ['list', 'hr.vacancy'],
  ])
  const open = useMutation({
    mutationFn: () => openVacancy(recordId),
    onSuccess: () => outcome.succeed(t('record.hr.vac.opened')),
    onError: outcome.fail,
  })
  const hold = useMutation({
    mutationFn: () => holdVacancy(recordId),
    onSuccess: () => outcome.succeed(t('record.hr.vac.held')),
    onError: outcome.fail,
  })

  const title = text(values, 'title') ?? short(recordId)
  // Open from a draft or from hold; the domain refuses the rest, and a button that always
  // fails teaches people to ignore failures.
  const primary: HeaderAction | null =
    mayUpdate && (status === 'draft' || status === 'on_hold')
      ? { id: 'open', label: t('record.hr.vac.open'), onClick: () => open.mutate(), disabled: open.isPending }
      : null
  const menu: MenuAction[] = [
    {
      id: 'copy-link',
      label: t('record.copyLink'),
      icon: 'link',
      onSelect: () => void navigator.clipboard?.writeText(window.location.href).catch(() => undefined),
    },
  ]
  if (mayUpdate && status === 'open') {
    menu.unshift({ id: 'hold', label: t('record.hr.vac.hold'), icon: 'clock', onSelect: () => hold.mutate() })
  }

  // ------------------------------------------------------------------ полоса и факты

  const openedOn = text(values, 'opened_on')
  const closedOn = text(values, 'closed_on')
  const targetOn = text(values, 'target_close_on')
  const order = ['draft', 'open', 'closed']
  const at = status === 'on_hold' ? 1 : order.indexOf(status ?? '')
  const steps: StageStep[] = [
    { id: 'draft', label: t('value.hr.vacancy.status.draft'), state: at > 0 ? 'done' : at === 0 ? 'current' : 'future' },
    {
      id: 'open',
      label: status === 'on_hold' ? t('value.hr.vacancy.status.on_hold') : t('value.hr.vacancy.status.open'),
      state: at > 1 ? 'done' : at === 1 ? 'current' : 'future',
      warning: status === 'on_hold',
      meta: openedOn ? t('record.hr.vac.openedOn', { date: day(openedOn, locale) }) : undefined,
    },
    {
      id: 'closed',
      label: t('value.hr.vacancy.status.closed'),
      state: at === 2 ? 'current' : 'future',
      meta: closedOn ? day(closedOn, locale) : targetOn ? t('record.hr.vac.targetMeta', { date: day(targetOn, locale) }) : undefined,
    },
  ]
  const ends = status === 'cancelled' ? [{ id: 'cancelled', label: t('value.hr.vacancy.status.cancelled'), tone: 'lost' as const, active: true }] : []

  const counts = funnel.data ?? {}
  const total = funnel.data ? Object.values(counts).reduce((sum, value) => sum + value, 0) : null
  const openDays = openedOn && status === 'open' ? calendarDays(openedOn, today()) : null
  const from = number(values, 'salary_from')
  const to = number(values, 'salary_to')
  const currency = text(values, 'currency')
  const facts: Fact[] = [
    {
      id: 'open',
      label: t('record.hr.vac.openFact'),
      value: openDays != null ? t('record.hr.days', { days: Math.max(openDays - 1, 0) }) : '—',
      sub: targetOn ? t('record.hr.vac.targetMeta', { date: day(targetOn, locale) }) : undefined,
    },
    { id: 'candidates', label: t('record.hr.vac.candidates'), value: total == null ? '—' : String(total), sub: t('record.hr.vac.candidatesNote') },
    { id: 'interview', label: t('hr.application.stage.interview'), value: funnel.data ? String(counts.interview ?? 0) : '—' },
    { id: 'offer', label: t('hr.application.stage.offer'), value: funnel.data ? String(counts.offer ?? 0) : '—' },
    {
      id: 'salary',
      label: t('record.hr.vac.salaryRange'),
      value:
        from == null && to == null
          ? '—'
          : [from == null ? null : money(from, currency, locale), to == null ? null : money(to, currency, locale)].filter(Boolean).join(' – '),
    },
  ]

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[{ label: t('nav.vacancies'), to: listPath('hr.vacancy', '/vacancies') }, { label: title }]}
        title={
          <span className="row" style={{ gap: 10 }}>
            {title}
            <StatusBadge entity="hr.vacancy" status={status} />
          </span>
        }
        meta={[
          labelOf(values, 'department_id') ? (
            <span key="dept" className="row" style={{ gap: 6 }}>
              <Icon name="building" size="sm" />
              <RefLink values={values} column="department_id" entity="hr.department" />
            </span>
          ) : null,
          labelOf(values, 'hiring_manager_id') ? (
            <span key="manager" className="row" style={{ gap: 6 }}>
              {t('record.hr.vac.hiringManager')} <RefLink values={values} column="hiring_manager_id" entity="hr.employee" avatar />
            </span>
          ) : null,
          labelOf(values, 'recruiter_id') ? (
            <span key="recruiter" className="row" style={{ gap: 6 }}>
              {t('record.hr.vac.recruiter')} <RefLink values={values} column="recruiter_id" entity="hr.employee" avatar />
            </span>
          ) : null,
        ]}
        primary={primary}
        menu={menu}
        menuLabel={t('record.more')}
      />

      <OutcomeAlerts failure={outcome.failure} notice={outcome.notice} />

      <StageBar steps={steps} ends={ends} ariaLabel={t('record.hr.vac.stages')} />
      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          ...(mayReadCandidates ? [{ id: 'candidates', label: t('card.tab.hrBoard'), count: board.data?.length ?? null }] : []),
          { id: 'funnel', label: t('card.tab.hrFunnel') },
          { id: 'details', label: t('record.hr.details') },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={mayReadCandidates || tab !== 'candidates' ? tab : 'funnel'}
        onChange={setTab}
        ariaLabel={t('record.hr.vac.sections')}
      >
        <div style={{ paddingTop: 14 }}>
          {tab === 'candidates' && mayReadCandidates && <VacancyBoard recordId={recordId} />}
          {(tab === 'funnel' || (tab === 'candidates' && !mayReadCandidates)) && <VacancyFunnel recordId={recordId} />}
          {tab === 'details' && <Panel title={t('record.hr.details')}>{form}</Panel>}
          {tab === 'history' && (
            <Panel title={t('record.history.changes')}>
              <HistoryPanel entity={context.entity} recordId={recordId} />
            </Panel>
          )}
        </div>
      </RecordTabs>
    </div>
  )
}

// ------------------------------------------------------------------ доска

/**
 * Доска кандидатов по стадиям (`.board` / `.col` / `.kcard`).
 *
 * Перевод — пунктом меню карточки, а не перетаскиванием: стадий пять, выбор однозначен, а
 * перетаскивание добавило бы способ промахнуться. Отказ спрашивает причину в окне и без неё
 * не отправляется: отказ без причины нечего сказать кандидату и нечего анализировать.
 */
export function VacancyBoard({ recordId }: { recordId: string }) {
  const { t, user, locale } = useApp()
  const [rejecting, setRejecting] = useState<ApplicationCard | null>(null)
  const [revoking, setRevoking] = useState<ApplicationCard | null>(null)
  const [reason, setReason] = useState('')
  const outcome = useOutcome([
    ['hr-board', recordId],
    ['hr-funnel', recordId],
    ['hr-conversion', recordId],
  ])
  const query = useQuery({
    queryKey: ['hr-board', recordId],
    queryFn: ({ signal }) => fetchBoard(recordId, signal),
    retry: false,
  })
  const mayUpdate = can(user, 'hr.candidate', 'update')
  const mayRevoke = can(user, 'hr.candidate', 'delete')

  const move = useMutation({
    mutationFn: (input: { id: string; stage: string }) => moveApplication(input.id, input.stage),
    onSuccess: () => outcome.succeed(t('card.hr.vac.stageChanged')),
    onError: outcome.fail,
  })
  const decline = useMutation({
    mutationFn: (id: string) => rejectApplication(id, reason.trim()),
    onSuccess: () => {
      setRejecting(null)
      setReason('')
      outcome.succeed(t('card.hr.vac.rejected'))
    },
    onError: outcome.fail,
  })
  const revoke = useMutation({
    mutationFn: (candidateId: string) => revokeConsent(candidateId),
    onSuccess: () => {
      setRevoking(null)
      outcome.succeed(t('card.hr.vac.consentRevoked'))
    },
    onError: (error) => {
      setRevoking(null)
      outcome.fail(error)
    },
  })

  return (
    <div className="stack" style={{ gap: 12 }}>
      <OutcomeAlerts failure={outcome.failure} notice={outcome.notice} />
      <Section query={query} errorTitle={t('record.hr.vac.boardFailed')}>
        {(rows: ApplicationCard[]) =>
          rows.length === 0 ? (
            <div className="state-box">
              <StateView kind="empty" title={t('card.hr.vac.noApplications')} description={t('record.hr.vac.noApplicationsText')} />
            </div>
          ) : (
            <div className="board" aria-label={t('record.hr.vac.board')}>
              {STAGES.map((stage) => {
                const column = rows.filter((row) => row.stage === stage)
                return (
                  <section key={stage} className="col hr-col" aria-label={t(`hr.application.stage.${stage}`)}>
                    <div className="col-h">
                      <h3 className="t">
                        <span>{t(`hr.application.stage.${stage}`)}</span>
                        <span className="spacer" />
                        <span className="count-pill">{column.length}</span>
                      </h3>
                    </div>
                    {column.length === 0 && <div className="t-sm muted hr-col-empty">{t('record.hr.vac.columnEmpty')}</div>}
                    {column.map((row) => {
                      const items: MenuAction[] = []
                      if (mayUpdate) {
                        for (const target of STAGES) {
                          if (target === row.stage) continue
                          items.push({
                            id: `move-${target}`,
                            label: t('record.hr.vac.moveTo', { stage: t(`hr.application.stage.${target}`) }),
                            icon: 'arrow-right',
                            onSelect: () => move.mutate({ id: row.id, stage: target }),
                          })
                        }
                        items.push({ id: 'reject', label: t('record.hr.vac.rejectOpen'), icon: 'close', onSelect: () => setRejecting(row) })
                      }
                      if (mayRevoke) {
                        items.push({ id: 'revoke', label: t('card.hr.vac.revokeConsent'), icon: 'trash', danger: true, onSelect: () => setRevoking(row) })
                      }
                      return (
                        <article key={row.id} className="kcard" style={{ cursor: 'default' }}>
                          <div className="row" style={{ gap: 6 }}>
                            <span className={row.candidateName ? 'ttl' : 'ttl mono'}>{candidateLabel(row, t)}</span>
                            <span className="spacer" />
                            {items.length > 0 && <MoreMenu compact items={items} label={t('record.hr.vac.cardActions')} />}
                          </div>
                          <div className="foot">
                            <span className="row nowrap" style={{ gap: 4 }}>
                              <Icon name="inbox" size="sm" />
                              {moment(row.appliedAt, locale)}
                            </span>
                            {row.stageChangedAt && (
                              <span className="row nowrap" style={{ gap: 4 }}>
                                <Icon name="clock" size="sm" />
                                {moment(row.stageChangedAt, locale)}
                              </span>
                            )}
                          </div>
                        </article>
                      )
                    })}
                  </section>
                )
              })}
            </div>
          )
        }
      </Section>

      <FormModal
        open={rejecting != null}
        title={t('record.hr.vac.rejectTitle')}
        subtitle={rejecting ? candidateLabel(rejecting, t) : undefined}
        onClose={() => setRejecting(null)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setRejecting(null)}>
              {t('card.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={decline.isPending || reason.trim() === ''}
              onClick={() => rejecting && decline.mutate(rejecting.id)}
            >
              {t('card.hr.vac.reject')}
            </button>
          </>
        }
      >
        <div className="field">
          <label className="label" htmlFor="hr-reject-reason">
            {t('card.hr.vac.reason')}
            <span className="req" aria-hidden="true">
              {' '}
              *
            </span>
          </label>
          <textarea id="hr-reject-reason" className="input" aria-required="true" value={reason} onChange={(event) => setReason(event.target.value)} />
          {/* Required, and said so before the click rather than by a refusal after it. */}
          <span className="help">{t('card.hr.vac.reasonRequired')}</span>
        </div>
      </FormModal>

      <ConfirmModal
        open={revoking != null}
        title={t('record.hr.vac.revokeTitle')}
        confirmLabel={t('card.hr.vac.revokeConsent')}
        pending={revoke.isPending}
        onCancel={() => setRevoking(null)}
        onConfirm={() => revoking && revoke.mutate(revoking.candidateId)}
      >
        <p>{t('record.hr.vac.revokeText')}</p>
        <p className="strong" style={{ color: 'var(--fg)' }}>
          {t('record.irreversible')}
        </p>
      </ConfirmModal>
    </div>
  )
}

/**
 * Имя кандидата с сервера; короткий идентификатор — только если имени не пришло (сервер
 * старше этого поля). Пустое имя не выдумывается.
 */
function candidateLabel(row: ApplicationCard, t: Translate): string {
  return row.candidateName && row.candidateName.trim() !== ''
    ? row.candidateName
    : t('record.hr.vac.candidate', { id: short(row.candidateId) })
}

// ------------------------------------------------------------------ воронка

/**
 * Воронка найма, конверсия, срок закрытия.
 *
 * Конверсия `null` — «стадию никто не проходил», а не ноль процентов: «все застряли» и
 * «никто не доходил» — разные факты. Срок закрытия `null`, пока вакансия открыта: срок не
 * наступил, а не равен нулю.
 */
export function VacancyFunnel({ recordId }: { recordId: string }) {
  const { t, locale } = useApp()
  const funnel = useQuery({ queryKey: ['hr-funnel', recordId], queryFn: ({ signal }) => fetchFunnel(recordId, signal), retry: false })
  const conversion = useQuery({
    queryKey: ['hr-conversion', recordId],
    queryFn: ({ signal }) => fetchConversion(recordId, signal),
    retry: false,
  })
  const timeToFill = useQuery({
    queryKey: ['hr-time-to-fill', recordId],
    queryFn: ({ signal }) => fetchTimeToFill(recordId, signal),
    retry: false,
  })

  return (
    <div className="layout-2-wide">
      <div className="rec-col">
        <Panel title={t('record.hr.vac.funnelTitle')} actions={<span className="t-sm muted">{t('record.hr.vac.funnelNote')}</span>}>
          <Section query={funnel} errorTitle={t('record.hr.vac.funnelFailed')}>
            {(counts: Record<string, number>) => {
              const entries = [...STAGES, 'rejected'].filter((stage) => counts[stage] !== undefined).map((stage) => [stage, counts[stage] ?? 0] as const)
              const max = Math.max(1, ...entries.map(([, count]) => count))
              return entries.length === 0 ? (
                <p className="secondary">{t('record.hr.vac.funnelEmpty')}</p>
              ) : (
                <div className="stack" style={{ gap: 10 }} aria-label={t('card.hr.vac.funnel')} role="list">
                  {entries.map(([stage, count]) => (
                    <div key={stage} className="funnel-row" role="listitem">
                      <span className="secondary">{t(`hr.application.stage.${stage}`)}</span>
                      <div className="hbar-track">
                        <div className="hbar" style={{ width: `${(count / max) * 100}%` }} />
                      </div>
                      <span className="num strong">{count}</span>
                    </div>
                  ))}
                </div>
              )
            }}
          </Section>
        </Panel>
        <Panel title={t('card.hr.vac.conversion')} flush>
          <Section query={conversion} errorTitle={t('record.hr.vac.funnelFailed')}>
            {(rows: ConversionView[]) =>
              rows.length === 0 ? (
                <div className="panel-b secondary">{t('record.hr.vac.funnelEmpty')}</div>
              ) : (
                <table className="tbl" aria-label={t('card.hr.vac.conversion')}>
                  <thead>
                    <tr>
                      <th>{t('card.hr.vac.stageFrom')}</th>
                      <th>{t('card.hr.vac.stageTo')}</th>
                      <th className="num">{t('card.hr.vac.entered')}</th>
                      <th className="num">{t('card.hr.vac.moved')}</th>
                      <th className="num">{t('card.hr.vac.percent')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={`${row.from}-${row.to}`}>
                        <td>{t(`hr.application.stage.${row.from}`)}</td>
                        <td>{t(`hr.application.stage.${row.to}`)}</td>
                        <td className="num">{row.entered}</td>
                        <td className="num">{row.moved}</td>
                        {/* A dash, not "0 %": nobody passed this stage. */}
                        <td className="num">{percent(row.percent, locale)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            }
          </Section>
        </Panel>
      </div>
      <div className="rec-col">
        <Panel title={t('card.hr.vac.timeToFill')}>
          <Section query={timeToFill} errorTitle={t('record.hr.vac.funnelFailed')}>
            {(data: { days: number | null }) => (
              <p>{data.days == null ? t('card.hr.vac.stillOpen') : t('card.hr.vac.filledInDays', { days: num(data.days, locale) })}</p>
            )}
          </Section>
        </Panel>
      </div>
    </div>
  )
}

/** Момент отклика: дата в поясе пользователя. */
function moment(value: string, locale: 'ru' | 'en'): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  const pad = (n: number) => String(n).padStart(2, '0')
  return formatDate(`${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`, locale)
}
